import * as dto from '@epicuro-next/data/baskets-dto';

import { getAttributeValues } from '../shared';

import { Mappings } from './basket.models';
import { findProduct, getProductKey } from './products.mapping';

/**
 * Group items by Divisions.
 */
export function groupItemsByDivision(items: dto.Item[], maps: Mappings) {
  const itemDivisionMap = getItemDivisionMap(items, maps);
  const flatMap = Object.entries(itemDivisionMap).flatMap(
    ([itemId, divisions]) => {
      return divisions.map((division) => ({ division, itemId }));
    },
  );

  return flatMap.reduce((result, next) => {
    return {
      ...result,
      [next.division]: [...(result[next.division] || []), next.itemId],
    };
  }, {} as { [division: string]: string[] });
}

/**
 * Creates map of items to Divisions.
 */
function getItemDivisionMap(items: dto.Item[], maps: Mappings) {
  const path = new Set<string>();

  return items.reduce((acc, item) => {
    const product = findProduct(item, maps.products);
    if (!product) {
      return acc;
    }

    const divisions = getDivisions(product, maps, path);

    return {
      ...acc,
      [item.itemId]: divisions,
    };
  }, {} as { [itemId: string]: string[] });
}

/**
 * Recursively collect division for the product.
 */
function getDivisions(
  product: dto.ReferencedProduct,
  maps: Mappings,
  path: Set<string>,
): string[] {
  if (!product) {
    return [];
  }

  if (path.has(product.productId)) {
    return [];
  }

  path.add(product.productId);

  const parentDivisions = product.masterProductId
    ? getDivisions(
        maps.products[
          getProductKey(product.brand.brandId, product.masterProductId)
        ],
        maps,
        path,
      )
    : [];

  const compositionDivisions = product.productCompositionPartReferences
    ? product.productCompositionPartReferences.flatMap((part) =>
        getDivisions(
          maps.products[
            getProductKey(part.product.brandId, part.product.productId)
          ],
          maps,
          path,
        ),
      )
    : [];

  const attributeDivisions = getAttributeValues(product, 'divisionb2b');

  path.delete(product.productId);

  const productDivision =
    attributeDivisions.pop() ??
    parentDivisions.pop() ??
    compositionDivisions.pop() ??
    'Other';

  return [productDivision];
}
